import {Component, Input, OnChanges, OnDestroy} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {AngularFireDatabase} from '@angular/fire/compat/database';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Subscription} from 'rxjs';
import {UserData} from '../../../../auth.service';
import {HospitalService} from '../../../../services/hospital.service';
import {AssessmentChange, HOSPITAL_QUESTIONNAIRE, QuestionnaireQuestion} from './hospital-questionnaire';

interface ChangeLogEntry {
  changedAt: number;
  changedBy: string;
  changes: AssessmentChange[];
}

interface Assessment {
  key: string;
  assessmentDate: string;
  assessedBy: string;
  answers: { [questionKey: string]: any };
  createdAt: number;
  createdBy: string;
  changeLog: ChangeLogEntry[];
}

@Component({
  selector: 'app-hospital-assessment',
  templateUrl: './hospital-assessment.component.html'
})
export class HospitalAssessmentComponent implements OnChanges, OnDestroy {
  @Input() hospitalId!: string;

  readonly questions = HOSPITAL_QUESTIONNAIRE;
  readonly userData = JSON.parse(localStorage.getItem('userData')!) as UserData;

  assessments: Assessment[] = [];
  form: FormGroup | null = null;
  editing: Assessment | null = null;
  expandedKey: string | null = null;
  saving = false;
  readonly today = new Date();

  private sub?: Subscription;

  constructor(private readonly db: AngularFireDatabase,
              private readonly snackBar: MatSnackBar,
              private readonly hospitalService: HospitalService) {}

  private get basePath(): string {
    // Same env-aware node as the hospital list (testHospitals in dev, Hospitals in prod).
    return `${this.hospitalService.env.hospitals}/${this.hospitalId}/assessments`;
  }

  ngOnChanges(): void {
    this.sub?.unsubscribe();
    if (!this.hospitalId) return;
    this.sub = this.db.object<any>(this.basePath).valueChanges().subscribe(val => {
      this.assessments = Object.entries(val ?? {}).map(([key, a]: [string, any]) => ({
        ...a,
        key,
        answers: a.answers ?? {},
        changeLog: Object.values(a.changeLog ?? {}) as ChangeLogEntry[]
      })).sort((a, b) => (b.assessmentDate ?? '').localeCompare(a.assessmentDate ?? '') || b.createdAt - a.createdAt);
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  startNew(): void {
    this.openForm(null);
  }

  edit(a: Assessment): void {
    this.openForm(a);
  }

  cancel(): void {
    this.form = null;
    this.editing = null;
  }

  toggleLog(a: Assessment): void {
    this.expandedKey = this.expandedKey === a.key ? null : a.key;
  }

  private openForm(a: Assessment | null): void {
    this.editing = a;
    const controls: { [k: string]: FormControl } = {
      assessmentDate: new FormControl(a?.assessmentDate ? this.fromIso(a.assessmentDate) : new Date(), Validators.required),
      assessedBy: new FormControl(a?.assessedBy ?? this.userData?.userName ?? '', Validators.required)
    };
    this.questions.forEach(q => controls[q.key] = new FormControl(a?.answers?.[q.key] ?? null));
    this.form = new FormGroup(controls);
  }

  async save(): Promise<void> {
    if (!this.form || this.form.invalid || this.saving) return;
    this.saving = true;
    const {assessmentDate: dateValue, assessedBy, ...rest} = this.form.value;
    const assessmentDate = this.toIso(dateValue);
    const answers: { [k: string]: any } = {};
    this.questions.forEach(q => {
      const v = rest[q.key];
      if (v !== null && v !== undefined && v !== '') answers[q.key] = v;
    });
    const now = Date.now();
    const who = this.userData?.userName ?? this.userData?.email ?? 'unknown';

    try {
      if (!this.editing) {
        await this.db.list(this.basePath).push({assessmentDate, assessedBy, answers, createdAt: now, createdBy: who});
      } else {
        const changes = this.diff(this.editing, {assessmentDate, assessedBy, answers});
        if (changes.length === 0) {
          this.snackBar.open('No changes to save', '', {duration: 2000});
          this.saving = false;
          return;
        }
        const path = `${this.basePath}/${this.editing.key}`;
        await this.db.object(path).update({assessmentDate, assessedBy, answers, updatedAt: now, updatedBy: who});
        await this.db.list(`${path}/changeLog`).push({changedAt: now, changedBy: who, changes});
      }
      this.snackBar.open('Assessment saved', '', {duration: 2000});
      this.cancel();
    } catch (err) {
      console.error('Error saving assessment', err);
      this.snackBar.open('Error saving assessment', '', {duration: 3000});
    }
    this.saving = false;
  }

  // Stored as local-date 'YYYY-MM-DD' strings so sorting and the change log stay simple.
  private toIso(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private fromIso(s: string): Date {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  private diff(old: Assessment, next: { assessmentDate: string; assessedBy: string; answers: { [k: string]: any } }): AssessmentChange[] {
    const changes: AssessmentChange[] = [];
    const push = (question: string, from: any, to: any) => {
      if (String(from ?? '') !== String(to ?? '')) changes.push({question, from: from ?? '', to: to ?? ''});
    };
    push('Assessment date', old.assessmentDate, next.assessmentDate);
    push('Assessed by', old.assessedBy, next.assessedBy);
    this.questions.forEach(q => push(q.label, old.answers[q.key], next.answers[q.key]));
    return changes;
  }

  answerFor(a: Assessment, q: QuestionnaireQuestion): any {
    return a.answers[q.key];
  }
}
